import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../api/cliente';
import {
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
  Vazio,
} from '../componentes/base';
import { Campo, Seletor } from '../componentes/campos';
import { ModalDarBaixa, type ParcelaParaBaixar } from '../componentes/DarBaixa';
import { MenuAcoes } from '../componentes/MenuAcoes';
import { ModalNovoDevedor } from '../componentes/NovoDevedor';
import { Paginacao, type Paginado } from '../componentes/Paginacao';
import { data, documento, moeda, plural, telefone } from '../util/formato';

interface ParcelaResumo {
  readonly id: string;
  readonly numero: number;
  readonly valor: number;
  readonly vencimento: string;
}

interface UltimaBaixa extends ParcelaResumo {
  readonly pagamento: {
    valorPago: number;
    dataPagamento: string;
    formaPagamento: string;
    observacoes: string | null;
    percentualHonorarios: number;
  };
}

interface Devedor {
  readonly id: string;
  readonly nome: string;
  readonly documento: string | null;
  readonly documentoValido: boolean;
  readonly telefone: string | null;
  readonly valorOriginal: number;
  readonly fase: string;
  readonly clinica: {
    id: string;
    nome: string;
    percentualHonorarios: number;
  } | null;
  readonly responsavel: { id: string; nome: string } | null;
  readonly totalPago: number;
  readonly parcelasAtrasadas: number;
  readonly proximoVencimento: string | null;
  readonly proximaParcela: ParcelaResumo | null;
  readonly ultimaBaixa: UltimaBaixa | null;
}

interface DescricaoFase {
  readonly chave: string;
  readonly curto: string;
  readonly rotulo: string;
  readonly cor: string;
}

export function PaginaDevedores() {
  const [params, setParams] = useSearchParams();
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [cadastrando, setCadastrando] = useState(false);
  const [baixando, setBaixando] = useState<{
    parcela: ParcelaParaBaixar;
    devedorId: string;
    nome: string;
    percentualHonorarios: number;
  } | null>(null);

  const filtro = (chave: string) => params.get(chave) ?? '';
  function definir(chave: string, valor: string) {
    const novo = new URLSearchParams(params);
    if (valor) novo.set(chave, valor);
    else novo.delete(chave);
    setParams(novo, { replace: true });
    setPagina(1);
  }

  const { data: fases } = useQuery({
    queryKey: ['fases'],
    queryFn: () => api.buscar<{ fases: DescricaoFase[] }>('/devedores/fases'),
    staleTime: Infinity,
  });

  const { data: clinicas } = useQuery({
    queryKey: ['clinicas', 'selecao'],
    queryFn: () => api.buscar<Array<{ id: string; nome: string }>>('/clinicas/selecao'),
  });

  const { data: equipe } = useQuery({
    queryKey: ['equipe', 'selecao'],
    queryFn: () => api.buscar<Array<{ id: string; nome: string }>>('/usuarios/selecao'),
  });

  const { data: lista, isLoading } = useQuery({
    queryKey: ['devedores', pagina, busca, params.toString()],
    queryFn: () =>
      api.buscar<Paginado<Devedor>>('/devedores', {
        pagina,
        limite: 50,
        busca,
        clinicaId: filtro('clinicaId'),
        fase: filtro('fase'),
        responsavelId: filtro('responsavelId'),
        emAtraso: filtro('emAtraso'),
        clausulaPenal: filtro('clausulaPenal'),
      }),
  });

  const porChave = new Map((fases?.fases ?? []).map((f) => [f.chave, f]));
  const temFiltro = [...params.keys()].length > 0 || busca !== '';

  function abrirPago(d: Devedor) {
    if (!d.proximaParcela || !d.clinica) return;
    setBaixando({
      parcela: {
        id: d.proximaParcela.id,
        numero: d.proximaParcela.numero,
        valor: d.proximaParcela.valor,
        vencimento: d.proximaParcela.vencimento,
      },
      devedorId: d.id,
      nome: d.nome,
      percentualHonorarios: d.clinica.percentualHonorarios,
    });
  }

  function abrirCorrigir(d: Devedor) {
    if (!d.ultimaBaixa || !d.clinica) {
      window.alert('Este caso ainda não tem baixa para corrigir.');
      return;
    }
    setBaixando({
      parcela: {
        id: d.ultimaBaixa.id,
        numero: d.ultimaBaixa.numero,
        valor: d.ultimaBaixa.valor,
        vencimento: d.ultimaBaixa.vencimento,
        pagamento: d.ultimaBaixa.pagamento,
      },
      devedorId: d.id,
      nome: d.nome,
      percentualHonorarios: d.clinica.percentualHonorarios,
    });
  }

  return (
    <>
      <CabecalhoDaPagina
        titulo="Carteira de devedores"
        descricao={
          lista ? plural(lista.total, 'caso encontrado', 'casos encontrados') : undefined
        }
        acoes={
          <Botao tipo="primario" onClick={() => setCadastrando(true)}>
            Novo devedor
          </Botao>
        }
      />

      <ModalNovoDevedor aberto={cadastrando} aoFechar={() => setCadastrando(false)} />

      <Cartao semPadding>
        <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 xl:grid-cols-5">
          <Campo
            rotulo="Busca"
            valor={busca}
            aoMudar={(v) => {
              setBusca(v);
              setPagina(1);
            }}
            placeholder="Nome, CPF ou telefone"
          />
          <Seletor
            rotulo="Clínica"
            valor={filtro('clinicaId')}
            aoMudar={(v) => definir('clinicaId', v)}
            vazio="Todas"
            opcoes={(clinicas ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))}
          />
          <Seletor
            rotulo="Fase"
            valor={filtro('fase')}
            aoMudar={(v) => definir('fase', v)}
            vazio="Todas"
            opcoes={(fases?.fases ?? []).map((f) => ({ valor: f.chave, rotulo: f.curto }))}
          />
          <Seletor
            rotulo="Responsável"
            valor={filtro('responsavelId')}
            aoMudar={(v) => definir('responsavelId', v)}
            vazio="Todas"
            opcoes={[
              { valor: 'sem', rotulo: '— sem responsável —' },
              ...(equipe ?? []).map((u) => ({ valor: u.id, rotulo: u.nome })),
            ]}
          />
          <Seletor
            rotulo="Situação"
            valor={
              filtro('emAtraso') === 'true'
                ? 'atraso'
                : filtro('clausulaPenal')
                  ? `multa-${filtro('clausulaPenal')}`
                  : ''
            }
            aoMudar={(v) => {
              const novo = new URLSearchParams(params);
              novo.delete('emAtraso');
              novo.delete('clausulaPenal');
              if (v === 'atraso') novo.set('emAtraso', 'true');
              if (v === 'multa-com') novo.set('clausulaPenal', 'com');
              if (v === 'multa-sem') novo.set('clausulaPenal', 'sem');
              setParams(novo, { replace: true });
              setPagina(1);
            }}
            vazio="Todas"
            opcoes={[
              { valor: 'atraso', rotulo: 'Com parcela em atraso' },
              { valor: 'multa-com', rotulo: 'Acordo COM cláusula penal' },
              { valor: 'multa-sem', rotulo: 'Acordo SEM cláusula penal' },
            ]}
          />
        </div>

        {temFiltro && (
          <div className="px-5 pb-4">
            <Botao
              tipo="discreto"
              onClick={() => {
                setParams(new URLSearchParams(), { replace: true });
                setBusca('');
                setPagina(1);
              }}
              className="px-0 text-xs"
            >
              Limpar filtros
            </Botao>
          </div>
        )}

        {isLoading ? (
          <Carregando />
        ) : !lista || lista.itens.length === 0 ? (
          <Vazio
            titulo={
              temFiltro
                ? 'Nenhum caso corresponde aos filtros aplicados.'
                : 'A carteira está vazia.'
            }
            saida={
              temFiltro ? (
                <Botao
                  onClick={() => {
                    setParams(new URLSearchParams(), { replace: true });
                    setBusca('');
                  }}
                >
                  Limpar filtros
                </Botao>
              ) : (
                <Link
                  to="/importar"
                  className="text-sm text-[color:var(--acento)] underline underline-offset-2"
                >
                  Importar a planilha de uma clínica
                </Link>
              )
            }
          />
        ) : (
          <>
            <div className="rolagem-lateral border-t">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[color:var(--superficie-afundada)] text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                    <th className="px-5 py-2.5 font-medium">Devedor</th>
                    <th className="px-3 py-2.5 font-medium">Fase</th>
                    <th className="px-3 py-2.5 font-medium">Responsável</th>
                    <th className="px-3 py-2.5 text-right font-medium">Dívida</th>
                    <th className="px-3 py-2.5 text-right font-medium">Recuperado</th>
                    <th className="px-3 py-2.5 text-right font-medium">Próx. vencimento</th>
                    <th className="px-5 py-2.5 text-right font-medium">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lista.itens.map((d) => {
                    const fase = porChave.get(d.fase);
                    return (
                      <tr
                        key={d.id}
                        className="border-b transition-colors last:border-0 hover:bg-[color:var(--superficie-afundada)]"
                      >
                        <td className="px-5 py-3">
                          <Link
                            to={`/devedores/${d.id}`}
                            className="font-medium text-[color:var(--texto)] hover:text-[color:var(--acento)]"
                          >
                            {d.nome}
                          </Link>
                          <p className="mt-0.5 font-mono text-[0.6875rem] text-[color:var(--texto-tenue)]">
                            {documento(d.documento)}
                            {d.documento && !d.documentoValido && (
                              <span
                                className="ml-1.5 font-sans"
                                style={{ color: 'var(--color-atencao)' }}
                                title="O dígito verificador não confere"
                              >
                                CPF suspeito
                              </span>
                            )}
                            {d.telefone ? (
                              <> · {telefone(d.telefone)}</>
                            ) : (
                              <span
                                className="ml-1.5 font-sans"
                                style={{ color: 'var(--color-atencao)' }}
                              >
                                sem telefone
                              </span>
                            )}
                          </p>
                          <p className="mt-0.5 text-[0.6875rem] text-[color:var(--texto-tenue)]">
                            {d.clinica?.nome}
                          </p>
                        </td>

                        <td className="px-3 py-3">
                          {fase && <Ficha cor={fase.cor}>{fase.curto}</Ficha>}
                          {d.parcelasAtrasadas > 0 && (
                            <p
                              className="mt-1 text-[0.6875rem]"
                              style={{ color: 'var(--color-vencido)' }}
                            >
                              {plural(
                                d.parcelasAtrasadas,
                                'parcela em atraso',
                                'parcelas em atraso',
                              )}
                            </p>
                          )}
                        </td>

                        <td className="px-3 py-3 text-xs text-[color:var(--texto-suave)]">
                          {d.responsavel?.nome ?? (
                            <span className="text-[color:var(--texto-tenue)]">—</span>
                          )}
                        </td>

                        <td className="tabular px-3 py-3 text-right">
                          {moeda(d.valorOriginal)}
                        </td>

                        <td className="tabular px-3 py-3 text-right">
                          {d.totalPago > 0 ? (
                            <span style={{ color: 'var(--color-recebido)' }}>
                              {moeda(d.totalPago)}
                            </span>
                          ) : (
                            <span className="text-[color:var(--texto-tenue)]">—</span>
                          )}
                        </td>

                        <td className="tabular px-3 py-3 text-right text-[color:var(--texto-suave)]">
                          {d.proximoVencimento ? data(d.proximoVencimento) : '—'}
                        </td>

                        <td className="px-5 py-3 text-right">
                          <MenuAcoes
                            rotulo={`Ações de ${d.nome}`}
                            itens={[
                              {
                                rotulo: 'Pago',
                                aoClicar: () => abrirPago(d),
                                desabilitado: !d.proximaParcela || !d.clinica,
                              },
                              {
                                rotulo: 'Corrigir',
                                aoClicar: () => abrirCorrigir(d),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Paginacao
              dados={lista}
              aoMudarPagina={setPagina}
              nomeDoItem={['caso', 'casos']}
            />
          </>
        )}
      </Cartao>

      <ModalDarBaixa
        aberto={baixando != null}
        aoFechar={() => setBaixando(null)}
        parcela={baixando?.parcela ?? null}
        devedorId={baixando?.devedorId ?? ''}
        nomeDoDevedor={baixando?.nome ?? ''}
        percentualHonorarios={baixando?.percentualHonorarios ?? 0}
      />
    </>
  );
}
